import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAuthCodeAPI,
  getHomeInfoAPI,
  getHomeDealsAPI,
  getHomeTimeAPI,
  getCheckAuthAPI,
  getShowOnboardingVideoAPI,
} from "../../lib/api/api";

const HOME_OVERVIEW_STORAGE_KEY = "homeOverviewCache";

const loadHomeOverviewFromStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.localStorage.getItem(HOME_OVERVIEW_STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error("Could not read cached home overview", error);
    return null;
  }
};

const saveHomeOverviewToStorage = (data) => {
  if (typeof window === "undefined" || !data) return;
  try {
    window.localStorage.setItem(
      HOME_OVERVIEW_STORAGE_KEY,
      JSON.stringify(data)
    );
  } catch (error) {
    console.error("Could not cache home overview", error);
  }
};

const cachedHomeData = loadHomeOverviewFromStorage();

const initialState = {
  loading: false,
  status: "loading",
  authData: null,
  homeData: cachedHomeData,
  homeStatus: cachedHomeData ? "cached" : "idle",
  homeError: null,
  membershipChecked: false,
  homeOverviewFetched: Boolean(cachedHomeData),
};

export const fetchAuthCode = createAsyncThunk(
  "data/checkUserAuth",
  async (code) => {
    const res = await getAuthCodeAPI(code);
    return res;
  }
);

export const fetchHomeOverview = createAsyncThunk(
  "data/fetchHomeOverview",
  async () => {
    const [info, deals, time, onboarding] = await Promise.all([
      getHomeInfoAPI(),
      getHomeDealsAPI(),
      getHomeTimeAPI(),
      getShowOnboardingVideoAPI(),
    ]);

    return {
      ...(info || {}),
      ...(deals || {}),
      ...(time || {}),
      showOnboardingVideo: Boolean(onboarding?.show_onboarding),
    };
  }
);

export const fetchCheckAuthAPI = createAsyncThunk(
  "data/getCheckAuthAPI",
  async () => {
    const res = await getCheckAuthAPI();
    return res;
  }
);

const authSlice = createSlice({
  name: "authCode",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthCode.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAuthCode.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload?.status;
      })
      .addCase(fetchAuthCode.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCheckAuthAPI.pending, (state) => {
        state.loading = true;
        state.membershipChecked = false;
      })
      .addCase(fetchCheckAuthAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload?.status;
        state.authData = action.payload;
        state.membershipChecked = true;
      })
      .addCase(fetchCheckAuthAPI.rejected, (state) => {
        state.loading = false;
        state.membershipChecked = true;
      })
      .addCase(fetchHomeOverview.pending, (state) => {
        state.homeStatus = "loading";
        state.homeOverviewFetched = false;
      })
      .addCase(fetchHomeOverview.fulfilled, (state, action) => {
        state.homeStatus = "succeeded";
        state.homeOverviewFetched = true;
        state.membershipChecked = true;
        state.homeData = {
          ...(state.homeData || {}),
          ...(action.payload || {}),
        };
        saveHomeOverviewToStorage(state.homeData);
      })
      .addCase(fetchHomeOverview.rejected, (state, action) => {
        state.homeStatus = "failed";
        state.error = action.error.message;
        state.homeOverviewFetched = true;
      });
  }
});

export default authSlice.reducer;
